import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { createCustomerPortalSession } from "@/integrations/backend/stripe";

type Props = {
  displayName: string;
  joinDateText: string;
  bioText: string;
  avatarUrl: string;
  nameInitial: string;
  completedWorkouts: number;
  totalPoints: number;
  metricsLoading?: boolean;
  onEditProfile?: () => void;
  onManageSubscription?: () => void;
};

export function ProfileSidebarCard({
  displayName,
  joinDateText,
  bioText,
  avatarUrl,
  nameInitial,
  completedWorkouts,
  totalPoints,
  metricsLoading = false,
  onEditProfile,
  onManageSubscription,
}: Props) {
  const [bioExpanded, setBioExpanded] = useState(false);

  return (
    <div className="hidden md:flex p-6 md:p-8 flex-col items-center md:items-start md:max-w-[280px] md:border-r border-primary/20">
      <div className="relative group mb-4">
        <Avatar className="w-28 h-28 border-4 border-dark-bg shadow-md">
          <AvatarImage src={avatarUrl} alt={displayName} />
          <AvatarFallback className="text-4xl bg-dark-slate text-white">
            {nameInitial}
          </AvatarFallback>
        </Avatar>
      </div>

      <h2 className="text-2xl font-bold text-white mb-1">{displayName}</h2>
      <div className="mb-4 text-sm text-gray-light">Joined {joinDateText}</div>

      {bioText && (
        <div className="w-full mb-4">
          <p className="text-sm text-gray-light">
            {bioExpanded || bioText.length <= 200
              ? bioText
              : `${bioText.slice(0, 200)}...`}
          </p>
          {!bioExpanded && bioText.length > 200 && (
            <Button
              variant="link"
              className="p-0 h-auto text-primary"
              onClick={() => setBioExpanded(true)}
            >
              View more
            </Button>
          )}
        </div>
      )}

      <Button
        onClick={onEditProfile}
        className="mb-3 w-full bg-primary text-black hover:bg-primary/90"
      >
        Edit Profile
      </Button>
      <Button
        variant="outline"
        className="mb-6 w-full border-primary/30 text-primary hover:bg-primary hover:text-black"
        onClick={async () => {
          if (onManageSubscription) {
            onManageSubscription();
          } else {
            try {
              const response = await createCustomerPortalSession();
              window.location.href = response.portal_url;
            } catch (error) {
              console.error("Failed to create customer portal session", error);
              // TODO: Show error message to user
            }
          }
        }}
      >
        Manage Subscription
      </Button>

      <div className="w-full space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-gray-light">Completed Workouts</span>
          <span className="font-semibold text-white">
            {metricsLoading ? "—" : completedWorkouts.toLocaleString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-gray-light">Total Points</span>
          <span className="font-semibold text-primary">
            {metricsLoading ? "—" : totalPoints.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}


